import type { EncounterPhotoApiItem } from 'features/Procedure/state/procedure-api.type'
import { useUploadEncounterPhotoMutation } from 'features/Procedure/state/procedure.api'
import { useCallback } from 'react'
import {
  buildEncounterPhotoUploadQueue,
  createEncounterPhotoUploadFailureError,
  getEncounterPhotoErrorDescription,
  getEncounterPhotoFileTooLargeMessage,
  isEncounterPhotoFileTooLarge,
  type EncounterPhotoUploadFailure,
  type PreparedEncounterPhotoUpload,
} from './photos.helpers'
import { addPhotoUploadBreadcrumb } from './photos.sentry'
import type { PhotoData } from './photos.types'

type PhotoCategory = 'before' | 'after'

type UploadEncounterPhotoFilesArgs = {
  category: PhotoCategory
  files: File[]
  existingPhotos: EncounterPhotoApiItem[]
  siteId: string
  patientId: number
  encounterUid: string
  onBeforeUpload?: (preparedUpload: PreparedEncounterPhotoUpload) => Promise<void> | void
  overrideFileName?: string
}

export type EncounterPhotoUploadResult = {
  uploadedPhotos: PhotoData[]
  failures: EncounterPhotoUploadFailure[]
}

type EncounterPhotoUploadOutcome =
  | {
      status: 'fulfilled'
      order: number
      photo: PhotoData
    }
  | {
      status: 'rejected'
      order: number
      failure: EncounterPhotoUploadFailure
    }

export const useEncounterPhotoUpload = () => {
  const [uploadEncounterPhoto] = useUploadEncounterPhotoMutation()

  return useCallback(
    async ({
      category,
      files,
      existingPhotos,
      siteId,
      patientId,
      encounterUid,
      onBeforeUpload,
      overrideFileName,
    }: UploadEncounterPhotoFilesArgs): Promise<EncounterPhotoUploadResult> => {
      if (files.length === 0) {
        return {
          uploadedPhotos: [],
          failures: [],
        }
      }

      const preparedUploads = buildEncounterPhotoUploadQueue({
        category,
        files,
        existingPhotos,
        overrideFileName,
      })

      const uploadOutcomes: EncounterPhotoUploadOutcome[] = []

      for (const preparedUpload of preparedUploads) {
        if (isEncounterPhotoFileTooLarge(preparedUpload.file)) {
          addPhotoUploadBreadcrumb(
            'Upload rejected: file too large',
            {
              order: preparedUpload.order,
              fileName: preparedUpload.sourceFileName,
              fileSize: preparedUpload.file.size,
            },
            'warning',
          )
          uploadOutcomes.push({
            status: 'rejected',
            order: preparedUpload.order,
            failure: {
              order: preparedUpload.order,
              fileName: preparedUpload.fileName,
              sourceFileName: preparedUpload.sourceFileName,
              description: getEncounterPhotoFileTooLargeMessage(),
            },
          })
          continue
        }

        try {
          await onBeforeUpload?.(preparedUpload)

          addPhotoUploadBreadcrumb('Uploading file to API', {
            order: preparedUpload.order,
            fileName: preparedUpload.fileName,
            fileSize: preparedUpload.file.size,
          })

          const formData = new FormData()
          formData.append('photo', preparedUpload.file, preparedUpload.fileName)
          formData.append('file_name', preparedUpload.fileName)
          formData.append('category', category)
          formData.append('note', '')

          const uploadResponse = await uploadEncounterPhoto({
            siteId,
            patientId,
            encounterUid,
            formData,
          }).unwrap()

          addPhotoUploadBreadcrumb('File uploaded successfully', {
            order: preparedUpload.order,
            photoId: uploadResponse.data?.photo_id,
          })

          uploadOutcomes.push({
            status: 'fulfilled',
            order: preparedUpload.order,
            photo: {
              id:
                uploadResponse.data?.photo_id?.toString() ||
                `photo-${Date.now()}-${preparedUpload.order + 1}`,
              imageUrl: uploadResponse.data?.thumbnail || uploadResponse.data?.url || '',
              label: uploadResponse.data?.filename || preparedUpload.fileName,
            },
          })
        } catch (error) {
          const errorDescription = getEncounterPhotoErrorDescription(error, 'Failed to upload photo.')

          addPhotoUploadBreadcrumb(
            'File upload failed',
            {
              order: preparedUpload.order,
              fileName: preparedUpload.sourceFileName,
              error: Array.isArray(errorDescription)
                ? errorDescription.join(' ')
                : errorDescription,
            },
            'error',
          )
          uploadOutcomes.push({
            status: 'rejected',
            order: preparedUpload.order,
            failure: {
              order: preparedUpload.order,
              fileName: preparedUpload.fileName,
              sourceFileName: preparedUpload.sourceFileName,
              description: errorDescription,
            },
          })
        }
      }
      const uploadedPhotos = uploadOutcomes
        .filter(
          (outcome): outcome is Extract<EncounterPhotoUploadOutcome, { status: 'fulfilled' }> =>
            outcome.status === 'fulfilled',
        )
        .sort((left, right) => left.order - right.order)
        .map((outcome) => outcome.photo)
      const failures = uploadOutcomes
        .filter(
          (outcome): outcome is Extract<EncounterPhotoUploadOutcome, { status: 'rejected' }> =>
            outcome.status === 'rejected',
        )
        .sort((left, right) => left.order - right.order)
        .map((outcome) => outcome.failure)

      if (uploadedPhotos.length === 0 && failures.length > 0) {
        addPhotoUploadBreadcrumb(
          'All uploads failed — throwing',
          {
            failureCount: failures.length,
            failures: failures.map((f) => ({
              order: f.order,
              fileName: f.sourceFileName,
              description: f.description,
            })),
          },
          'error',
        )
        throw createEncounterPhotoUploadFailureError(failures, 'Failed to upload photo(s).')
      }

      return {
        uploadedPhotos,
        failures,
      }
    },
    [uploadEncounterPhoto],
  )
}
